import { ACTIONS, applyAction, getLegalMoves } from '../engine';
import type { Action, Card, GameState } from '../engine';

export type AiLearningWeights = {
  protectPointsMult: number;
  feedBuildAvoidMult: number;
  dominanceBonusMult: number;
  takeBiasMult: number;
  avoidDropWhenTakeAvailableMult: number;
  avoidLowValuePileTakeMult: number;
  saveRoyalForLastMult: number;
  avoidSingleAnchorBuildMult: number;
  avoidEarlyHighBuildMult: number;
  preferRoyalCoverMult: number;
};

export type AiLearningProfile = {
  version: 1;
  updatedAt: string;
  weights: AiLearningWeights;
};

const STORAGE_KEY = 'omulongo_ai_profile_v1';

const DEFAULT_WEIGHTS: AiLearningWeights = {
  protectPointsMult: 1.0,
  feedBuildAvoidMult: 1.0,
  dominanceBonusMult: 1.0,
  takeBiasMult: 1.0,
  avoidDropWhenTakeAvailableMult: 1.0,
  avoidLowValuePileTakeMult: 1.0,
  saveRoyalForLastMult: 1.0,
  avoidSingleAnchorBuildMult: 1.0,
  avoidEarlyHighBuildMult: 1.0,
  preferRoyalCoverMult: 1.0,
};

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function safeNumber(value: unknown): number | null {
  if (typeof value !== 'number') return null;
  if (!Number.isFinite(value)) return null;
  return value;
}

export function loadLocalAiProfile(): AiLearningProfile {
  if (typeof localStorage === 'undefined') {
    return { version: 1, updatedAt: new Date().toISOString(), weights: { ...DEFAULT_WEIGHTS } };
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) throw new Error('missing');
    const parsed = JSON.parse(raw) as Partial<AiLearningProfile>;
    const w = parsed.weights || ({} as Partial<AiLearningWeights>);
    const weights: AiLearningWeights = {
      protectPointsMult: clamp(safeNumber(w.protectPointsMult) ?? 1, 0.8, 1.8),
      feedBuildAvoidMult: clamp(safeNumber(w.feedBuildAvoidMult) ?? 1, 0.8, 1.8),
      dominanceBonusMult: clamp(safeNumber(w.dominanceBonusMult) ?? 1, 0.8, 1.8),
      takeBiasMult: clamp(safeNumber(w.takeBiasMult) ?? 1, 0.8, 1.8),
      avoidDropWhenTakeAvailableMult: clamp(safeNumber(w.avoidDropWhenTakeAvailableMult) ?? 1, 0.8, 1.8),
      avoidLowValuePileTakeMult: clamp(safeNumber(w.avoidLowValuePileTakeMult) ?? 1, 0.8, 1.8),
      saveRoyalForLastMult: clamp(safeNumber(w.saveRoyalForLastMult) ?? 1, 0.8, 1.8),
      avoidSingleAnchorBuildMult: clamp(safeNumber((w as any).avoidSingleAnchorBuildMult) ?? 1, 0.8, 1.8),
      avoidEarlyHighBuildMult: clamp(safeNumber((w as any).avoidEarlyHighBuildMult) ?? 1, 0.8, 1.8),
      preferRoyalCoverMult: clamp(safeNumber((w as any).preferRoyalCoverMult) ?? 1, 0.8, 1.8),
    };
    return { version: 1, updatedAt: typeof parsed.updatedAt === 'string' ? parsed.updatedAt : new Date().toISOString(), weights };
  } catch {
    return { version: 1, updatedAt: new Date().toISOString(), weights: { ...DEFAULT_WEIGHTS } };
  }
}

export function saveLocalAiProfile(profile: AiLearningProfile): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
}

let communityProfile: AiLearningProfile | null = null;
let localProfile: AiLearningProfile = loadLocalAiProfile();

export function setLocalAiProfile(profile: AiLearningProfile): void {
  localProfile = profile;
  saveLocalAiProfile(localProfile);
}

export function sanitizeAiLearningProfile(value: unknown): AiLearningProfile | null {
  if (!value || typeof value !== 'object') return null;
  const v = value as Partial<AiLearningProfile>;
  const w = (v as any).weights as Partial<AiLearningWeights> | undefined;
  if (!w || typeof w !== 'object') return null;

  const weights: AiLearningWeights = {
    protectPointsMult: clamp(safeNumber(w.protectPointsMult) ?? 1, 0.8, 1.8),
    feedBuildAvoidMult: clamp(safeNumber(w.feedBuildAvoidMult) ?? 1, 0.8, 1.8),
    dominanceBonusMult: clamp(safeNumber(w.dominanceBonusMult) ?? 1, 0.8, 1.8),
    takeBiasMult: clamp(safeNumber(w.takeBiasMult) ?? 1, 0.8, 1.8),
    avoidDropWhenTakeAvailableMult: clamp(safeNumber((w as any).avoidDropWhenTakeAvailableMult) ?? 1, 0.8, 1.8),
    avoidLowValuePileTakeMult: clamp(safeNumber((w as any).avoidLowValuePileTakeMult) ?? 1, 0.8, 1.8),
    saveRoyalForLastMult: clamp(safeNumber((w as any).saveRoyalForLastMult) ?? 1, 0.8, 1.8),
    avoidSingleAnchorBuildMult: clamp(safeNumber((w as any).avoidSingleAnchorBuildMult) ?? 1, 0.8, 1.8),
    avoidEarlyHighBuildMult: clamp(safeNumber((w as any).avoidEarlyHighBuildMult) ?? 1, 0.8, 1.8),
    preferRoyalCoverMult: clamp(safeNumber((w as any).preferRoyalCoverMult) ?? 1, 0.8, 1.8),
  };

  return {
    version: 1,
    updatedAt: typeof (v as any).updatedAt === 'string' ? (v as any).updatedAt : new Date().toISOString(),
    weights,
  };
}

export function setCommunityAiProfile(profile: AiLearningProfile | null): void {
  communityProfile = profile;
}

export function getActiveAiWeights(): AiLearningWeights {
  // Community profile acts as a gentle multiplier, but local profile is primary.
  const community = communityProfile?.weights || DEFAULT_WEIGHTS;
  const local = localProfile.weights;
  return {
    protectPointsMult: clamp(local.protectPointsMult * community.protectPointsMult, 0.7, 2.0),
    feedBuildAvoidMult: clamp(local.feedBuildAvoidMult * community.feedBuildAvoidMult, 0.7, 2.0),
    dominanceBonusMult: clamp(local.dominanceBonusMult * community.dominanceBonusMult, 0.7, 2.0),
    takeBiasMult: clamp(local.takeBiasMult * community.takeBiasMult, 0.7, 2.0),
    avoidDropWhenTakeAvailableMult: clamp(
      local.avoidDropWhenTakeAvailableMult * community.avoidDropWhenTakeAvailableMult,
      0.7,
      2.0
    ),
    avoidLowValuePileTakeMult: clamp(local.avoidLowValuePileTakeMult * community.avoidLowValuePileTakeMult, 0.7, 2.0),
    saveRoyalForLastMult: clamp(local.saveRoyalForLastMult * community.saveRoyalForLastMult, 0.7, 2.0),
    avoidSingleAnchorBuildMult: clamp(local.avoidSingleAnchorBuildMult * community.avoidSingleAnchorBuildMult, 0.7, 2.0),
    avoidEarlyHighBuildMult: clamp(local.avoidEarlyHighBuildMult * community.avoidEarlyHighBuildMult, 0.7, 2.0),
    preferRoyalCoverMult: clamp(local.preferRoyalCoverMult * community.preferRoyalCoverMult, 0.7, 2.0),
  };
}

type SessionStats = {
  aiTeamId: string | null;
  droppedValuable: number;
  fedBuild: number;
  spentPointCardNoGain: number;
  droppedWhileTakeAvailable: number;
  tookLowValuePile: number;
  spentRoyalNotLast: number;
  builtWithSingleAnchor: number;
  builtHighEarly: number;
  missedRoyalCover: number;
};

let session: SessionStats = {
  aiTeamId: null,
  droppedValuable: 0,
  fedBuild: 0,
  spentPointCardNoGain: 0,
  droppedWhileTakeAvailable: 0,
  tookLowValuePile: 0,
  spentRoyalNotLast: 0,
  builtWithSingleAnchor: 0,
  builtHighEarly: 0,
  missedRoyalCover: 0,
};

function numericRankValue(rank: string): number | null {
  if (rank === 'A') return 1;
  if (rank === 'J' || rank === 'Q' || rank === 'K') return null;
  return Number(rank);
}

function isSpade(card: Pick<Card, 'suit'>): boolean {
  return card.suit === 'S';
}

function isTenDiamonds(card: Pick<Card, 'rank' | 'suit'>): boolean {
  return card.rank === '10' && card.suit === 'D';
}

function isAce(card: Pick<Card, 'rank'>): boolean {
  return card.rank === 'A';
}

function isTwoSpades(card: Pick<Card, 'rank' | 'suit'>): boolean {
  return card.rank === '2' && card.suit === 'S';
}

function isValuable(card: Card): boolean {
  const v = numericRankValue(card.rank);
  const highNumeric = v !== null && v >= 7 && v <= 10;
  return isAce(card) || isTenDiamonds(card) || isTwoSpades(card) || isSpade(card) || highNumeric || (card.rank === 'J' || card.rank === 'Q' || card.rank === 'K');
}

function getPlayedCardFromAction(prev: GameState, playerId: string, action: Action): Card | null {
  const anyAction = action as any;
  const playedId =
    (typeof anyAction.cardId === 'string' && anyAction.cardId) ||
    (typeof anyAction.cardIdFromHand === 'string' && anyAction.cardIdFromHand) ||
    (typeof anyAction.playedCardIdFromHand === 'string' && anyAction.playedCardIdFromHand) ||
    (typeof anyAction.cardIdFromHand1 === 'string' && anyAction.cardIdFromHand1) ||
    null;
  if (!playedId) return null;
  return (prev.hands[playerId] || []).find((c) => c.id === playedId) || null;
}

function determineAiTeamId(state: GameState, aiPlayerId: string): string | null {
  const player = state.players.find((p) => p.id === aiPlayerId);
  return player?.teamId ?? null;
}

export function resetAiLearningSession(): void {
  session = {
    aiTeamId: null,
    droppedValuable: 0,
    fedBuild: 0,
    spentPointCardNoGain: 0,
    droppedWhileTakeAvailable: 0,
    tookLowValuePile: 0,
    spentRoyalNotLast: 0,
    builtWithSingleAnchor: 0,
    builtHighEarly: 0,
    missedRoyalCover: 0,
  };
}

function isLastRound(state: GameState): boolean {
  return state.meta.roundIndex >= state.meta.totalRounds - 1;
}

function isRoyal(card: Card): boolean {
  return card.rank === 'J' || card.rank === 'Q' || card.rank === 'K';
}

function isLowValuePile(cards: Card[]): boolean {
  if (cards.length >= 10) return false;
  const spades = cards.filter(isSpade).length;
  if (spades >= 2) return false;
  if (cards.some(isAce)) return false;
  if (cards.some(isTenDiamonds)) return false;
  if (cards.some(isTwoSpades)) return false;
  return true;
}

function countNumericValue(cards: Card[], value: number): number {
  return cards.reduce((sum, card) => sum + (numericRankValue(card.rank) === value ? 1 : 0), 0);
}

function getTopCard(cards: Card[]): Card | null {
  return cards.length ? cards[cards.length - 1] : null;
}

export function recordAiMove(prev: GameState, action: Action, aiPlayerId: string): void {
  if (typeof window === 'undefined') return;
  if (action.type === ACTIONS.NEW_GAME || action.type === ACTIONS.START_ROUND || action.type === ACTIONS.DEAL_INITIAL) return;

  if (!session.aiTeamId) {
    session.aiTeamId = determineAiTeamId(prev, aiPlayerId);
  }

  const played = getPlayedCardFromAction(prev, aiPlayerId, action);
  if (!played) return;

  const myTeamId = determineAiTeamId(prev, aiPlayerId);
  const oppPlayerIds = prev.players.filter((p) => p.teamId !== myTeamId).map((p) => p.id);
  const opponentHands = oppPlayerIds.flatMap((pid) => prev.hands[pid] || []);
  const opponentHasAnyPile =
    prev.builds.some((b) => determineAiTeamId(prev, b.ownerPlayerId) !== myTeamId) ||
    prev.stays.some((s) => determineAiTeamId(prev, s.ownerPlayerId) !== myTeamId);

  if (action.type === ACTIONS.DROP) {
    const legal = getLegalMoves(prev, aiPlayerId);
    const hasTake = legal.some(
      (m) =>
        m.type === ACTIONS.TAKE_SINGLE ||
        m.type === ACTIONS.TAKE_SUBSET ||
        m.type === ACTIONS.TAKE_PILE ||
        m.type === ACTIONS.TAKE_SINGLE_ADD_TO_BUILD ||
        m.type === ACTIONS.TAKE_SINGLE_ADD_TO_STAY ||
        m.type === ACTIONS.TAKE_SUBSET_ADD_TO_BUILD ||
        m.type === ACTIONS.TAKE_SUBSET_ADD_TO_STAY ||
        m.type === ACTIONS.TAKE_PILE_ADD_TO_BUILD ||
        m.type === ACTIONS.TAKE_PILE_ADD_TO_STAY
    );
    if (hasTake) session.droppedWhileTakeAvailable += 1;
  }

  // Missed royal cover: our top is a numeric that can reinforce an opponent pile with a known complement in hand,
  // and we had a royal TAKE_SINGLE available but didn't take it.
  {
    const legal = getLegalMoves(prev, aiPlayerId);
    const royalTakeAvailable = legal.some((m: any) => {
      if (m.type !== ACTIONS.TAKE_SINGLE) return false;
      const cardIdFromHand = typeof m.cardIdFromHand === 'string' ? m.cardIdFromHand : null;
      if (!cardIdFromHand) return false;
      const handCard = (prev.hands[aiPlayerId] || []).find((c) => c.id === cardIdFromHand) || null;
      return Boolean(handCard && isRoyal(handCard));
    });
    const teamTop = myTeamId ? getTopCard(prev.collections[myTeamId]?.cards || []) : null;
    const topV = teamTop ? numericRankValue(teamTop.rank) : null;
    if (royalTakeAvailable && topV !== null && opponentHasAnyPile) {
      const oppPiles = [
        ...prev.builds.filter((b) => determineAiTeamId(prev, b.ownerPlayerId) !== myTeamId),
        ...prev.stays.filter((s) => determineAiTeamId(prev, s.ownerPlayerId) !== myTeamId),
      ];
      const threatened = oppPiles.some((pile: any) => {
        const target = pile.value as number;
        const needed = target - topV;
        if (needed < 1 || needed > 10) return false;
        return opponentHands.some((c) => numericRankValue(c.rank) === needed);
      });
      if (threatened && action.type !== ACTIONS.TAKE_SINGLE) {
        session.missedRoyalCover += 1;
      }
    }
  }

  if (action.type === ACTIONS.TAKE_PILE || action.type === ACTIONS.TAKE_PILE_ADD_TO_BUILD || action.type === ACTIONS.TAKE_PILE_ADD_TO_STAY) {
    const anyAction = action as any;
    const target = anyAction.target || anyAction.takeTarget;
    if (target?.type === 'build') {
      const pile = prev.builds.find((b) => b.id === target.pileId);
      if (pile && isLowValuePile(pile.cards)) session.tookLowValuePile += 1;
    } else if (target?.type === 'stay') {
      const pile = prev.stays.find((s) => s.id === target.pileId);
      if (pile && isLowValuePile(pile.cards)) session.tookLowValuePile += 1;
    }
  }

  const next = applyAction(prev, action);
  const teamId = session.aiTeamId;
  const gained = teamId ? (next.collections[teamId]?.cards?.length || 0) - (prev.collections[teamId]?.cards?.length || 0) : 0;

  // Penalize creating a build where we only have one remaining matching value card after the move.
  if (
    action.type === ACTIONS.BUILD_CREATE ||
    action.type === ACTIONS.BUILD_SOLID_FROM_TABLE ||
    action.type === ACTIONS.BUILD_SOLID_FROM_OPP_TOP ||
    action.type === ACTIONS.INCREASE_OPP_TOP_TO_MATCH ||
    action.type === ACTIONS.BUILD_UPDATE
  ) {
    const ownedBuildValues = new Set<number>(
      next.builds
        .filter((b) => determineAiTeamId(next, b.ownerPlayerId) === myTeamId)
        .map((b) => b.value)
    );
    const candidate = Array.from(ownedBuildValues).sort((a, b) => b - a)[0] ?? null;
    if (candidate !== null) {
      const teamHandCards = (myTeamId ? next.players.filter((p) => p.teamId === myTeamId).flatMap((p) => next.hands[p.id] || []) : []) as Card[];
      if (countNumericValue(teamHandCards, candidate) === 1) {
        session.builtWithSingleAnchor += 1;
      }
      const remainingCards = next.deck.length + Object.values(next.hands).reduce((sum, hand) => sum + hand.length, 0);
      const earlyGame = remainingCards >= 22;
      const high = candidate >= 8;
      const oppHasCollectionCards = myTeamId
        ? next.players.some((p) => p.teamId !== myTeamId && (next.collections[p.teamId]?.cards?.length || 0) > 0)
        : false;
      const numericOnTable = next.tableLoose.filter((c) => numericRankValue(c.rank) !== null).length;
      if (earlyGame && high && !oppHasCollectionCards && numericOnTable < 2) {
        session.builtHighEarly += 1;
      }
    }
  }

  if (isRoyal(played) && isLastRound(prev)) {
    const nextHand = next.hands[aiPlayerId] || [];
    if (nextHand.length > 0 && gained === 0) {
      session.spentRoyalNotLast += 1;
    }
  }

  if (action.type === ACTIONS.DROP && isValuable(played)) {
    session.droppedValuable += 1;
  }

  // Heuristic: if we drop a numeric card that helps complete any opponent build/stay value with a common complement,
  // treat as feeding.
  if (action.type === ACTIONS.DROP) {
    const v = numericRankValue(played.rank);
    if (v !== null) {
      const myTeamId = determineAiTeamId(prev, aiPlayerId);
      const oppPlayers = prev.players.filter((p) => p.teamId !== myTeamId).map((p) => p.id);
      const oppHands = oppPlayers.flatMap((pid) => prev.hands[pid] || []);
      const oppPiles = [
        ...prev.builds.filter((b) => determineAiTeamId(prev, b.ownerPlayerId) !== myTeamId),
        ...prev.stays.filter((s) => determineAiTeamId(prev, s.ownerPlayerId) !== myTeamId),
      ];
      const fed = oppPiles.some((pile: any) => {
        const target = pile.value as number;
        if (typeof target !== 'number') return false;
        const needed = target - v;
        if (needed <= 0 || needed > 10) return false;
        return oppHands.some((c) => numericRankValue(c.rank) === needed);
      });
      if (fed) session.fedBuild += 1;
    }
  }

  // Spending point cards without immediate gain.
  if (gained <= 0) {
    const pointish = isAce(played) || isTenDiamonds(played) || isTwoSpades(played) || isSpade(played);
    if (pointish && action.type !== ACTIONS.DROP) {
      session.spentPointCardNoGain += 1;
    }
  }
}

export function finalizeAiLearningGame(params: { winnerTeamId: string | null; aiTeamId: string | null }): AiLearningProfile {
  // Safe + slow: only adjust slightly, clamp tightly, and decay toward 1.0 on wins.
  const lrLose = 0.03;
  const lrWinDecay = 0.02;

  const outcomeKnown = Boolean(params.winnerTeamId && params.aiTeamId);
  const won = outcomeKnown && params.winnerTeamId === params.aiTeamId;
  const w = { ...localProfile.weights };

  if (!outcomeKnown || won) {
    // Move weights gently back toward 1.
    w.protectPointsMult = w.protectPointsMult + (1 - w.protectPointsMult) * lrWinDecay;
    w.feedBuildAvoidMult = w.feedBuildAvoidMult + (1 - w.feedBuildAvoidMult) * lrWinDecay;
    w.dominanceBonusMult = w.dominanceBonusMult + (1 - w.dominanceBonusMult) * lrWinDecay;
    w.takeBiasMult = w.takeBiasMult + (1 - w.takeBiasMult) * lrWinDecay;
  } else {
    const bump = (count: number) => 1 + lrLose * clamp(count / 4, 0, 1);
    w.protectPointsMult = clamp(w.protectPointsMult * bump(session.droppedValuable + session.spentPointCardNoGain), 0.8, 1.8);
    w.feedBuildAvoidMult = clamp(w.feedBuildAvoidMult * bump(session.fedBuild), 0.8, 1.8);
    w.dominanceBonusMult = clamp(w.dominanceBonusMult * (1 + lrLose * 0.5), 0.8, 1.8);
    w.takeBiasMult = clamp(w.takeBiasMult * (1 + lrLose * 0.25), 0.8, 1.8);
    w.avoidDropWhenTakeAvailableMult = clamp(w.avoidDropWhenTakeAvailableMult * bump(session.droppedWhileTakeAvailable), 0.8, 1.8);
    w.avoidLowValuePileTakeMult = clamp(w.avoidLowValuePileTakeMult * bump(session.tookLowValuePile), 0.8, 1.8);
    w.saveRoyalForLastMult = clamp(w.saveRoyalForLastMult * bump(session.spentRoyalNotLast), 0.8, 1.8);
    w.avoidSingleAnchorBuildMult = clamp(w.avoidSingleAnchorBuildMult * bump(session.builtWithSingleAnchor), 0.8, 1.8);
    w.avoidEarlyHighBuildMult = clamp(w.avoidEarlyHighBuildMult * bump(session.builtHighEarly), 0.8, 1.8);
    w.preferRoyalCoverMult = clamp(w.preferRoyalCoverMult * bump(session.missedRoyalCover), 0.8, 1.8);
  }

  localProfile = { version: 1, updatedAt: new Date().toISOString(), weights: w };
  saveLocalAiProfile(localProfile);
  resetAiLearningSession();
  return localProfile;
}

export function getLocalAiProfile(): AiLearningProfile {
  return localProfile;
}
